import { useMemo } from "react";
import { Card, Stack, Typography } from "@altimateai/lego";
import DatabricksCostChartSkeleton from "@/pages/databricks/summary/components/DatabricksCostChartSkeleton";
import AutoTuneSavingsChart from "@/pages/jobs/auto-tune/charts/AutoTuneSavingsChart";
import { mergeSeries } from "@/pages/jobs/auto-tune/AutoTuneSavingsCard";
import { useClusterCost } from "../hooks";
import { useClusterAutoTuneSavingsTrends } from "./hooks";

interface Props {
  clusterId: string;
  instanceId?: number | string;
  // Calendar-filter bounds (YYYY-MM-DD) from the cluster-detail page. The
  // card renders nothing until both bounds are available.
  startDate?: string;
  endDate?: string;
}

interface CostByPeriod {
  [period: string]: number;
}

// Single stacked daily chart on the cluster Auto Tune tab. Mirrors the
// jobs Cost Savings chart but without the realized series — clusters have
// no realized-savings mart, so only Spend (ClickHouse cluster cost) and
// Possible Savings (spend × savings-pct, server-computed) stack. Reuses
// the jobs ``mergeSeries`` (actual is empty → realized stays 0) and the
// shared chart with ``hideRealized``.
const ClusterAutoTuneSavingsCard = ({ clusterId, instanceId, startDate, endDate }: Props) => {
  const hasDateRange = Boolean(startDate && endDate);

  const savingsQuery = useClusterAutoTuneSavingsTrends(
    clusterId,
    instanceId,
    startDate ?? "",
    endDate ?? "",
    hasDateRange
  );
  const costQuery = useClusterCost(
    {
      cluster_id: clusterId,
      start_date: startDate ?? "",
      end_date: endDate ?? "",
      granularity: "day",
    },
    Boolean(clusterId) && hasDateRange
  );

  const costByPeriod = useMemo<CostByPeriod>(() => {
    const map: CostByPeriod = {};
    for (const point of costQuery.data?.timeseries ?? []) {
      map[point.period] = point.estimated_cost ?? 0;
    }
    return map;
  }, [costQuery.data]);

  const points = useMemo(
    () => mergeSeries(savingsQuery.data, costByPeriod),
    [savingsQuery.data, costByPeriod]
  );

  if (!hasDateRange) {
    return null;
  }

  const isLoading = savingsQuery.isLoading || costQuery.isLoading;

  return (
    <Card className="al-p-4">
      <Stack direction="column" gap="none" className="al-mb-4">
        <Typography size="lg" weight="medium">
          Cost Savings
        </Typography>
        <Typography size="xs" className="al-text-muted-foreground">
          Daily · spend + possible savings
        </Typography>
      </Stack>
      {isLoading ? (
        <DatabricksCostChartSkeleton />
      ) : (
        <AutoTuneSavingsChart data={points} granularity="day" hideRealized />
      )}
    </Card>
  );
};

export default ClusterAutoTuneSavingsCard;
